/**
 * Centralized TPMS Color System
 * Single source of truth for all tire pressure monitoring system colors and status determination
 */

// Tire Status Enum
export enum TireStatus {
  UNHEALTHY = 'UNHEALTHY',
  WARNING = 'WARNING',
  HEALTHY = 'HEALTHY',
  NO_DATA = 'NO_DATA',
  NO_SENSOR = 'NO_SENSOR',
}

// All TPMS tire colors in one place
export const TPMS_COLORS = {
  // Active states
  UNHEALTHY: '#EB5757', // Red
  WARNING: '#F2994A', // Orange
  HEALTHY: '#27AE60', // Green
  NO_DATA: '#ebd081', // Yellowish
  NO_SENSOR: '#7D7D7D', // Gray

  // Stale states (>24h old data)
  STALE_UNHEALTHY: '#823131', // Darker Red
  STALE_WARNING: '#855429', // Darker Orange
  STALE_HEALTHY: '#155d33', // Darker Green
  STALE_NO_DATA: '#9a7c40', // Darker Yellowish
} as const;

// Card background colors for each status
export const TPMS_CARD_BG_COLORS = {
  [TireStatus.UNHEALTHY]: 'rgba(235, 87, 87, 0.3)', // Red background
  [TireStatus.WARNING]: 'rgba(242, 153, 74, 0.3)', // Orange background
  [TireStatus.HEALTHY]: 'rgba(57, 148, 95, 0.23)', // Green background
  [TireStatus.NO_DATA]: 'rgba(197, 175, 107, 0.67)', // Yellowish background
  [TireStatus.NO_SENSOR]: 'rgba(125, 125, 125, 0.1)', // Gray background
} as const;

export const TPMS_STALE_CARD_BG_COLORS = {
  [TireStatus.UNHEALTHY]: 'rgba(235, 193, 79, 0.3)', // Yellow (stale → always yellow)
  [TireStatus.WARNING]: 'rgba(235, 193, 79, 0.3)', // Yellow (stale → always yellow)
  [TireStatus.HEALTHY]: 'rgba(235, 193, 79, 0.3)', // Yellow (stale → always yellow)
  [TireStatus.NO_DATA]: 'rgba(235, 193, 79, 0.3)', // Yellow (stale → always yellow)
  [TireStatus.NO_SENSOR]: 'rgba(125, 125, 125, 0.1)', // Gray background
} as const;

/**
 * Central function to determine tire color based on status and staleness
 * @param status - Tire health status
 * @param isStale - Whether data is >24h old\
 * @returns Hex color code
 */
export function getTpmsTireColor(
  status: TireStatus,
  isStale?: boolean
): string {
  if (status === TireStatus.NO_SENSOR) return TPMS_COLORS.NO_SENSOR;

  // No data or stale (>24h) → Yellow
  if (status === TireStatus.NO_DATA || isStale === true)
    return TPMS_COLORS.NO_DATA;

  // Threshold breach + live data → Red
  if (status === TireStatus.UNHEALTHY) return TPMS_COLORS.UNHEALTHY;

  // Warning + live (temperature only) → Orange
  if (status === TireStatus.WARNING) return TPMS_COLORS.WARNING;

  return TPMS_COLORS.HEALTHY;
}

export function getTpmsTireTextColor(
  status: TireStatus,
  isStale?: boolean
): string {
  if (status === TireStatus.NO_DATA || isStale === true) return '#4a5568';

  return 'white';
}

// Configuration for warning availability
export const TPMS_WARNING_CONFIG = {
  pressure: false,
  temperature: true,
};

/**
 * Determine tire status based on value and limits
 * @param value - Current tire value (pressure/temp)
 * @param limits - {min, max, warning}
 * @param hasSensor - Whether sensor is installed
 * @param metricType - 'pressure' | 'temperature'
 * @returns TireStatus enum
 */
export function determineTireStatus(
  value: number | null | undefined,
  limits: { min?: number; max?: number; warning?: number },
  hasSensor: boolean,
  metricType: 'pressure' | 'temperature' = 'pressure' // Default to pressure for safety
): TireStatus {
  if (!hasSensor) return TireStatus.NO_SENSOR;

  if (value === null || value === undefined) return TireStatus.NO_DATA;

  const safeMin = limits.min ?? -Infinity;
  const safeMax = limits.max ?? Infinity;
  const safeWarning = limits.warning ?? safeMax;

  // Unhealthy: Value outside min/max range
  if (value < safeMin || value > safeMax) return TireStatus.UNHEALTHY;

  // Warning: Value in warning range (ONLY if enabled for this metric)
  const isWarningEnabled = TPMS_WARNING_CONFIG[metricType] ?? false;

  if (isWarningEnabled && value >= safeWarning && value <= safeMax) {
    return TireStatus.WARNING;
  }

  // Healthy: Value within acceptable range
  return TireStatus.HEALTHY;
}

// Legend configuration for UI consistency
// labelKey references keys in src/i18n/locales/*.json — resolve with t() at render time
export const TPMS_LEGEND = [
  {
    color: TPMS_COLORS.HEALTHY,
    labelKey: 'healthy',
    status: TireStatus.HEALTHY,
  },
  {
    color: TPMS_COLORS.UNHEALTHY,
    labelKey: 'unhealthy',
    status: TireStatus.UNHEALTHY,
  },
  {
    color: TPMS_COLORS.WARNING,
    labelKey: 'warning',
    status: TireStatus.WARNING,
  },
  {
    color: TPMS_COLORS.NO_DATA,
    labelKey: 'stale_no_data_24h',
    status: TireStatus.NO_DATA,
  },
  {
    color: TPMS_COLORS.NO_SENSOR,
    labelKey: 'no_sensor',
    status: TireStatus.NO_SENSOR,
  },
];
